import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { SessionService } from '../services/session.service';
import { I18nService } from '../services/i18n.service';

/**
 * Resolve the tenant brand from the current URL. When served from
 * `<brand>.onlineordering.getsonic.co` the leading label is the brand; otherwise
 * (localhost, IP, apex) we fall back to the configured `brandSubdomain`.
 */
function resolveBrandSubdomain(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const suffix = '.' + environment.brandBaseDomain;
  if (host.endsWith(suffix)) {
    const label = host.slice(0, host.length - suffix.length).split('.').pop();
    if (label) return label;
  }
  return environment.brandSubdomain;
}

/** Attaches brand, language, and bearer-token headers to API requests. */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly brandSubdomain = resolveBrandSubdomain();

  constructor(private session: SessionService, private i18n: I18nService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Match our API whether called relatively (/api/... via the dev proxy) or
    // absolutely (environment.apiBase in production). Never touch asset/CDN calls.
    const isApi = req.url.startsWith('/api/') || (!!environment.apiBase && req.url.startsWith(environment.apiBase));
    if (!isApi) return next.handle(req);

    let headers = req.headers
      .set('X-Brand-Subdomain', this.brandSubdomain)
      .set('Accept-Language', this.i18n.lang);

    const token = this.session.token;
    if (token) headers = headers.set('Authorization', `Bearer ${token}`);

    // Device location → server resolves the nearest branch + delivery distances.
    const c = this.session.coords;
    if (c) headers = headers.set('X-Latitude', String(c.lat)).set('X-Longitude', String(c.lng));

    return next.handle(req.clone({ headers }));
  }
}
